import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
	Box,
	Typography,
	CircularProgress,
} from '@material-ui/core';
import {
	GetApp,
} from '@material-ui/icons';
import {useOrders} from '../context/OrdersContext';
import API from '../../../../network/dashboardAPI';
import xlsx from 'json-as-xlsx';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		paddingBottom: theme.spacing(2),
	},
	section: {
		marginBottom: theme.spacing(3),
	},
	sectionTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(1),
	},
	fieldsList: {
		maxHeight: 300,
		overflow: 'auto',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		padding: theme.spacing(1),
	},
}));

const ExportDialog = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const {exportDialogOpen, statusFilter, searchText, dateRange, basketTypeFilter, orderBy, order: orderType} = state;

	const [reportType, setReportType] = useState('0'); // 0: Orders report, 1: Product Order Tracking
	const [processing, setProcessing] = useState(false);

	const getStatusLabel = (status) => {
		const statusMap = {
			'2': 'New',
			'3': 'Accepted',
			'4': 'Packed',
			'5': 'On The Way',
			'6': 'Delivered',
			'7': 'Cancelled',
			'8': 'Arrived',
			'-1': 'Waiting Payment',
		};
		return statusMap[status.toString()] || 'Unknown';
	};

	const handleExport = async () => {
		if (reportType === '0') {
			// Orders report
			await extractOrdersReport();
		} else {
			// Product Order Tracking
			await extractProductNeverOrder();
		}
	};

	const extractOrdersReport = async () => {
		setProcessing(true);

		try {
			// Parse status filter
			let tmpStatus = statusFilter;
			let statusList;

			if (tmpStatus === '-1') {
				statusList = [-1];
			} else {
				statusList = tmpStatus.toString().split('').map(x => parseInt(x));
			}

			// Format dates to match old UI format (YYYY-MM-DD only, no time)
			let formattedStartDate = '';
			let formattedEndDate = '';

			if (dateRange.start) {
				formattedStartDate = moment(dateRange.start).format('YYYY-MM-DD');
			}
			if (dateRange.end) {
				formattedEndDate = moment(dateRange.end).format('YYYY-MM-DD');
			}

			// "All" date filter: no window, so neither a real date nor the current
			// month fallback may be sent -- otherwise the export would silently
			// scope itself to a range the table never showed.
			const isAllDates = !formattedStartDate && !formattedEndDate;

			const query = {
				statusList: JSON.stringify(statusList),
				search: searchText,
				startDate: formattedStartDate,
				endDate: formattedEndDate,
				orderField: orderBy,
				orderType: orderType,
			};

			if (isAllDates) {
				query.dateFilter = 'all';
			} else {
				// Default dates - current month (first day to last day)
				const date = new Date();
				const y = date.getFullYear();
				const m = date.getMonth();
				query.startDateDefault = moment(new Date(y, m, 1)).format('YYYY-MM-DD');
				query.endDateDefault = moment(new Date(y, m + 1, 0)).format('YYYY-MM-DD');
			}

			if (basketTypeFilter) {
				query.type = basketTypeFilter;
			}

			// Calculate total days
			let totalDays = -1;
			if (dateRange.end && dateRange.start) {
				const difference = new Date(dateRange.end) - new Date(dateRange.start);
				totalDays = Math.ceil(difference / (1000 * 3600 * 24));
			}

			// Check date range limit (6 months = 184 days)
			if (totalDays >= 0 && totalDays > 184) {
				alert('Only available in 6 months range');
				setProcessing(false);
				return;
			}

			const response = await API.getOrderListWithoutPagination(query).then(res => res.json());

			if (response.success) {
				const ordersList = response.data.list || [];
				const newOrdersList = [];

				ordersList.forEach((order) => {
					// Handle null values
					if (!order.driver) {
						order.driver = {first_name: 'N/A', id: 'N/A', last_name: 'N/A'};
					}
					if (!order.store_on_going_slot) {
						order.store_on_going_slot = {start_time: 'N/A', end_time: 'N/A'};
					}
					if (!order.list_customer_address) {
						order.list_customer_address = {
							first_name: 'N/A',
							last_name: 'N/A',
							mobile_number: 'N/A',
							house_number: 'N/A',
							apartment_name: 'N/A',
							street_details: 'N/A',
							area_details: 'N/A',
							city: 'N/A',
							delivery_instructions: 'N/A',
						};
					}
					if (!order.customer_detail) {
						order.customer_detail = {user_promos: ''};
					}
					if (!order.order_payment_detail) {
						order.order_payment_detail = {amount: 0};
					}

					newOrdersList.push({
						order_id: order.order_id,
						first_name: order.list_customer_address.first_name,
						last_name: order.list_customer_address.last_name,
						mobile_number: order.list_customer_address.mobile_number,
						house_number: order.list_customer_address.house_number,
						apartment_name: order.list_customer_address.apartment_name,
						street_details: order.list_customer_address.street_details,
						area_details: order.list_customer_address.area_details,
						city: order.list_customer_address.city,
						delivery_instructions: order.list_customer_address.delivery_instructions,
						comments: order.comments,
						placedAt: order.placedAt,
						start_time: order.store_on_going_slot.start_time,
						end_time: order.store_on_going_slot.end_time,
						driver_id: order.driver.id,
						driver_firstname: order.driver.first_name,
						driver_lastname: order.driver.last_name,
						coupon_code_id: order.coupon_code_id,
						coupon_discount_amount: order.coupon_discount_amount,
						wallet_discount_amount: order.wallet_payment,
						user_promos: order.customer_detail.user_promos,
						item_total: order.item_total,
						delivery_fee: order.delivery_fee,
						amount: order.order_payment_detail.amount,
						Platform: order.Platform,
						payment_method_name: order.payment_method_name,
					});
				});

				// Generate XLSX
				const data = [
					{
						sheet: 'Orders',
						columns: [
							{label: 'order_id', value: 'order_id'},
							{label: 'first_name', value: 'first_name'},
							{label: 'last_name', value: 'last_name'},
							{label: 'mobile_number', value: 'mobile_number'},
							{label: 'house_number', value: 'house_number'},
							{label: 'apartment_name', value: 'apartment_name'},
							{label: 'street_details', value: 'street_details'},
							{label: 'area_details', value: 'area_details'},
							{label: 'city', value: 'city'},
							{label: 'delivery_instructions', value: 'delivery_instructions'},
							{label: 'comments', value: 'comments'},
							{label: 'placedAt', value: 'placedAt'},
							{label: 'start_time', value: 'start_time'},
							{label: 'end_time', value: 'end_time'},
							{label: 'driver_id', value: 'driver_id'},
							{label: 'driver_firstname', value: 'driver_firstname'},
							{label: 'driver_lastname', value: 'driver_lastname'},
							{label: 'coupon_code_id', value: 'coupon_code_id'},
							{label: 'coupon_discount_amount', value: 'coupon_discount_amount'},
							{label: 'wallet_discount_amount', value: 'wallet_discount_amount'},
							{label: 'user_promos', value: 'user_promos'},
							{label: 'item_total', value: 'item_total'},
							{label: 'delivery_fee', value: 'delivery_fee'},
							{label: 'amount', value: 'amount'},
							{label: 'Platform', value: 'Platform'},
							{label: 'payment_method_name', value: 'payment_method_name'},
						],
						content: newOrdersList,
					},
				];

				const settings = {
					fileName: `orders_report_${moment().format('YYYY-MM-DD_HHmmss')}`,
					extraLength: 3,
					writeOptions: {},
				};

				xlsx(data, settings);
				actions.closeExportDialog();
			} else {
				alert(response.message || 'Failed to fetch orders');
			}
		} catch (error) {
			console.error('Export error:', error);
			alert('Export failed: ' + error.message);
		} finally {
			setProcessing(false);
		}
	};

	const extractProductNeverOrder = async () => {
		setProcessing(true);

		try {
			const response = await API.getProductNeverOrderWithoutPagination().then(res => res.json());

			if (response.success) {
				const productList = response.data.list || [];
				const newProductList = [];

				productList.forEach((pro) => {
					newProductList.push({
						item_id: pro.item_id,
						name: pro.name,
						product_code: pro.product_code,
						order_quantity: pro.order_item_details?.length > 0
							? pro.order_item_details[0]?.order_quantity
							: pro.order_item_details?.length || 0,
						status: pro.is_deleted === 0 ? 'Active' : 'Inactive',
					});
				});

				// Generate XLSX
				const data = [
					{
						sheet: 'Products',
						columns: [
							{label: 'item_id', value: 'item_id'},
							{label: 'name', value: 'name'},
							{label: 'product_code', value: 'product_code'},
							{label: 'order_quantity', value: 'order_quantity'},
							{label: 'status', value: 'status'},
						],
						content: newProductList,
					},
				];

				const settings = {
					fileName: `product_never_ordered_${moment().format('YYYY-MM-DD_HHmmss')}`,
					extraLength: 3,
					writeOptions: {},
				};

				xlsx(data, settings);
				actions.closeExportDialog();
			} else {
				alert(response.message || 'Failed to fetch products');
			}
		} catch (error) {
			console.error('Export error:', error);
			alert('Export failed: ' + error.message);
		} finally {
			setProcessing(false);
		}
	};

	return (
		<Dialog
			open={exportDialogOpen}
			onClose={actions.closeExportDialog}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle className={classes.dialogTitle}>
				<Box display="flex" alignItems="center" justifyContent="center" gap={1}>
					<GetApp color="primary"/>
					<Typography variant="h6">Extract Report</Typography>
				</Box>
			</DialogTitle>

			<DialogContent style={{paddingTop: 24}}>
				<FormControl component="fieldset">
					<RadioGroup
						value={reportType}
						onChange={(e) => setReportType(e.target.value)}
					>
						<FormControlLabel
							value="0"
							control={<Radio color="primary"/>}
							label="Orders report"
						/>
						<FormControlLabel
							value="1"
							control={<Radio color="primary"/>}
							label="Product Order Tracking"
						/>
					</RadioGroup>
				</FormControl>
			</DialogContent>

			<DialogActions>
				<Button
					onClick={handleExport}
					color="primary"
					variant="contained"
					disabled={processing}
					startIcon={processing ? <CircularProgress size={20}/> : <GetApp/>}
				>
					{processing ? 'Extracting...' : 'Extract'}
				</Button>
				<Button
					onClick={actions.closeExportDialog}
					disabled={processing}
				>
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ExportDialog;
